import { AspectRatio, Flex, Image, motion } from '@hover/blueprint';
import type { FlexProps } from '@hover/blueprint';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import { exteriorSamples } from 'src/modules/homeownerApp/components/Home/SampleImages/exteriorSamples';
import { interiorSamples } from 'src/modules/homeownerApp/components/Home/SampleImages/interiorSamples';
import { BottomText } from 'src/modules/homeownerApp/components/Projects/Menu/AddSavedToProject/BottomText';

import exteriorOriginal from 'assets/img/homeownerApp/samples/exterior/exteriorSample.png';
import interiorOriginal from 'assets/img/homeownerApp/samples/interior/interiorSample.jpg';

import { useProTranslate } from '../../hooks';

// TODO: Replace with acutal styles once we have the new sample images.
export const styles = [
  'Coastal',
  'Contemporary',
  'Farmhouse',
  'Mediterranean',
  'Mid-Century Modern',
  'Traditional',
];

// TODO: Replace this with acutal logic once we have the new sample images.
export const getBackgroundImage = ({
  style,
  spaceType,
  isBeforeSelected = false,
}: {
  style?: string;
  spaceType: string;
  isBeforeSelected: boolean;
}) => {
  if (isBeforeSelected) {
    if (spaceType === 'Exterior') {
      return exteriorOriginal;
    }
    return interiorOriginal;
  }
  switch (style) {
    case 'Coastal': {
      return spaceType === 'Exterior'
        ? exteriorSamples.find(sample => sample.style === 'Coastal')?.jpeg
        : interiorSamples.find(sample => sample.style === 'Coastal')?.jpeg;
    }
    case 'Contemporary': {
      return spaceType === 'Exterior'
        ? exteriorSamples.find(sample => sample.style === 'Contemporary')?.jpeg
        : interiorSamples.find(sample => sample.style === 'Contemporary')?.jpeg;
    }
    case 'Cottage': {
      return spaceType === 'Exterior'
        ? exteriorSamples.find(sample => sample.style === 'Cottage')?.jpeg
        : interiorSamples.find(sample => sample.style === 'Cottage')?.jpeg;
    }
    case 'Craftsman': {
      return spaceType === 'Exterior'
        ? exteriorSamples.find(sample => sample.style === 'Craftsman')?.jpeg
        : interiorSamples.find(sample => sample.style === 'Craftsman')?.jpeg;
    }
    case 'Farmhouse': {
      return spaceType === 'Exterior'
        ? exteriorSamples.find(sample => sample.style === 'Farmhouse')?.jpeg
        : interiorSamples.find(sample => sample.style === 'Farmhouse')?.jpeg;
    }
    case 'Mediterranean': {
      return spaceType === 'Exterior'
        ? exteriorSamples.find(sample => sample.style === 'Mediterranean')?.jpeg
        : interiorSamples.find(sample => sample.style === 'Mediterranean')
            ?.jpeg;
    }
    case 'Mid-Century Modern': {
      return spaceType === 'Exterior'
        ? exteriorSamples.find(sample => sample.style === 'Mid-Century Modern')
            ?.jpeg
        : interiorSamples.find(sample => sample.style === 'Mid-Century Modern')
            ?.jpeg;
    }
    case 'Monochrome': {
      return spaceType === 'Exterior'
        ? exteriorSamples.find(sample => sample.style === 'Monochrome')?.jpeg
        : interiorSamples.find(sample => sample.style === 'Monochrome')?.jpeg;
    }
    case 'Rustic': {
      return spaceType === 'Exterior'
        ? exteriorSamples.find(sample => sample.style === 'Rustic')?.jpeg
        : interiorSamples.find(sample => sample.style === 'Rustic')?.jpeg;
    }
    case 'Traditional': {
      return spaceType === 'Exterior'
        ? exteriorSamples.find(sample => sample.style === 'Traditional')?.jpeg
        : interiorSamples.find(sample => sample.style === 'Traditional')?.jpeg;
    }
    default: {
      return spaceType === 'Exterior'
        ? exteriorSamples[Math.floor(Math.random() * styles.length)].jpeg
        : interiorSamples[Math.floor(Math.random() * styles.length)].jpeg;
    }
  }
};

const MotionImage = motion(Image);

interface ExampleCardProps extends FlexProps {
  spaceType: string;
  styleName: string;
  isBeforeSelected: boolean;
}

const SampleImage = ({
  spaceType,
  styleName,
  isBeforeSelected = true,
  ...rest
}: ExampleCardProps) => {
  const t = useProTranslate();
  const sampleImageUrl = getBackgroundImage({
    style: styleName,
    isBeforeSelected,
    spaceType,
  });

  const imageDescription = `${
    isBeforeSelected ? '' : 'Before: '
  }${styleName}, ${spaceType}`;

  return (
    <Flex alignItems="flex-start" flexDirection="column" width="100%" {...rest}>
      <AspectRatio ratio={3 / 4} width="100%">
        <AnimatePresence initial={false} mode="popLayout">
          <MotionImage
            alt={imageDescription}
            animate={{ opacity: 1 }}
            borderRadius={350}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            key={imageDescription}
            src={sampleImageUrl}
          />
        </AnimatePresence>
      </AspectRatio>
      <BottomText
        ml={200}
        mt={400}
        primaryText={spaceType}
        secondaryText={isBeforeSelected ? t('before') : styleName}
      />
    </Flex>
  );
};

export { SampleImage };
